import { useAuth0 } from '@auth0/auth0-react';
import { ErrorBanner, PageHeader } from '@my/ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { TmsHistoryButton } from '@/components/HistoryButtons';
import { CargoDefinitionsSection } from './CargoDefinitionsSection';

/** Table names read as jargon here. */
function cargoEntityLabel(entityType: string): string {
  switch (entityType) {
    case 'CargoDefinition':
      return 'Cargo type';
    case 'CargoDefinitionVariant':
      return 'Cargo variant';
    default:
      return entityType;
  }
}

/**
 * Settings → Cargo Definitions — how the organisation's goods travel (iso
 * tank, pallet, container…), each with its variants, equipment, units and
 * properties.
 *
 * <p>
 * Its own page, between Central Configuration and Products, rather than a card
 * on Central Configuration: it is the configuration operators return to most,
 * and it is the partner of Products — a cargo definition says HOW something
 * travels, a product says WHAT is in it. The "Use Products" switch lives in
 * this page's toolbar for that reason.
 * </p>
 *
 * <p>
 * It still reads two lists that stay on Central Configuration — service tags
 * and the organisation's equipment types — and shares that page's capability,
 * so whoever can reach one can reach the other.
 * </p>
 */
export function CargoDefinitionsPage() {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);
  const { t } = useTranslation(['configuration', 'common']);

  return (
    <div className="flex h-full flex-col px-2">
      <PageHeader
        title={t('configuration:cargoDefinitions.title')}
        description={t('configuration:cargoDefinitions.pageDescription')}
      >
        {/* Variants roll up to their cargo definition in the TMS audit, so one
            aggregate type covers the whole page. */}
        <TmsHistoryButton
          organisationId={orgId}
          aggregateType="CargoDefinition"
          wholeType
          subject={t('configuration:cargoDefinitions.title')}
          entityLabel={cargoEntityLabel}
          variant="subtle"
        />
      </PageHeader>

      <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto pb-4">
        {!orgId && (
          <ErrorBanner
            title={t('common:errors.noOrgLinkedTitle')}
            message={t('common:errors.noOrgLinkedMessage')}
          />
        )}

        <CargoDefinitionsSection organisationId={orgId} />
      </main>
    </div>
  );
}
